import 'react-swipeable-list/dist/styles.css'

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Download, Info, Play, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactPlayer from 'react-player'
import { useSearchParams } from 'react-router'
import { SwipeableList, SwipeableListItem, SwipeAction, TrailingActions } from 'react-swipeable-list'
import { toast } from 'sonner'

import { DownloadProgress } from '@/components/shared/download-progress'
import { DownloadUrlForm } from '@/components/shared/download-url-form'
import { Header } from '@/components/shared/header'
import { StorageUsage } from '@/components/shared/storage-usage'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useGoBack } from '@/hooks/useGoBack'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { db, type StoredExternalVideo } from '@/lib/db'
import { formatBytes } from '@/lib/utils'

import { useExternalVideoDownload } from './hooks/useExternalVideoDownload'
import { VideoInfoDialog } from './video-info-dialog'

type SortableVideoItemProps = {
  video: StoredExternalVideo
  onSelect: (video: StoredExternalVideo) => void
  onInfo: (video: StoredExternalVideo) => void
  onDelete: (video: StoredExternalVideo) => void
  showSeparator: boolean
}

const useThumbnailUrl = (blob: Blob | null) => {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!blob) {
      setUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(blob)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [blob])
  return url
}

const ThumbnailImage = ({ blob, alt, className }: { blob: Blob; alt: string; className?: string }) => {
  const url = useThumbnailUrl(blob)
  if (!url) return null
  return <img src={url} alt={alt} className={className} />
}

const SortableVideoItem = ({ video, onSelect, onInfo, onDelete, showSeparator }: SortableVideoItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: video.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const trailingActions = (
    <TrailingActions>
      <SwipeAction onClick={() => onDelete(video)}>
        <div className="flex flex-col items-center justify-center gap-1 px-4 bg-red-500 text-white h-full">
          <Trash2 size={20} />
          <span className="text-xs">削除</span>
        </div>
      </SwipeAction>
    </TrailingActions>
  )

  return (
    <div ref={setNodeRef} style={style} className="w-full my-1">
      <SwipeableListItem
        trailingActions={trailingActions}
        className="w-full"
        threshold={0.3}
        fullSwipe={false}
        blockSwipe={false}
      >
        <div className="flex items-center cursor-pointer w-full">
          {/* ドラッグハンドル */}
          <div {...attributes} {...listeners} className="shrink-0 px-2 cursor-grab active:cursor-grabbing">
            <div className="flex flex-col gap-1">
              <div className="w-1 h-1 rounded-full bg-muted-foreground" />
              <div className="w-1 h-1 rounded-full bg-muted-foreground" />
              <div className="w-1 h-1 rounded-full bg-muted-foreground" />
            </div>
          </div>

          <div onClick={() => onSelect(video)} className="flex items-center gap-3 flex-1 min-w-0">
            {/* サムネイル */}
            <div className="relative w-32 h-20 shrink-0 bg-muted rounded overflow-hidden">
              {video.thumbnailBlob ? (
                <ThumbnailImage blob={video.thumbnailBlob} alt={video.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Play size={32} className="text-muted-foreground" />
                </div>
              )}
            </div>

            {/* 動画情報 */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium truncate">{video.title}</h3>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                <span>{new Date(video.savedAt).toLocaleDateString('ja-JP')}</span>
                {video.fileSize && <span>{formatBytes(video.fileSize)}</span>}
              </div>
            </div>
          </div>

          {/* 情報ボタン */}
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation()
              onInfo(video)
            }}
          >
            <Info size={20} />
          </Button>
        </div>
      </SwipeableListItem>
      {showSeparator && <Separator className="mt-1" />}
    </div>
  )
}

export const CustomVideo = () => {
  const goBack = useGoBack()
  const [searchParams, setSearchParams] = useSearchParams()
  const [url, setUrl] = useState(() => searchParams.get('url') ?? '')
  const [quality, setQuality] = useState<'high' | 'medium' | 'low'>('high')
  const [videos, setVideos] = useState<StoredExternalVideo[]>([])
  const [selectedVideo, setSelectedVideo] = useState<StoredExternalVideo | null>(null)
  const [totalSize, setTotalSize] = useState(0)
  const [infoVideo, setInfoVideo] = useState<StoredExternalVideo | null>(null)

  const { downloadState, downloadVideo, cancelDownload } = useExternalVideoDownload()
  const { isOnline } = useOnlineStatus()

  // URLパラメータからURLを読み取った後、クエリパラメータをクリーンアップ
  useEffect(() => {
    if (searchParams.has('url')) {
      setSearchParams(new URLSearchParams(), { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px移動するまでドラッグ開始しない
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms長押しでドラッグ開始
        tolerance: 5, // 5pxの誤差を許容
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // 保存された動画のリストを取得
  const loadVideos = useCallback(async () => {
    let allVideos = await db.externalVideos.toArray()

    // マイグレーション: orderフィールドがない動画に追加
    const needsMigration = allVideos.some((video) => video.order === undefined)
    if (needsMigration) {
      await Promise.all(
        allVideos.map((video, index) => {
          if (video.order === undefined) {
            return db.externalVideos.update(video.id, { order: index })
          }
          return Promise.resolve()
        }),
      )
      // 更新後に再取得
      allVideos = await db.externalVideos.toArray()
    }

    // orderでソート
    allVideos.sort((a, b) => a.order - b.order)
    setVideos(allVideos)

    // 合計容量を計算
    const total = allVideos.reduce((sum, video) => sum + (video.fileSize || 0), 0)
    setTotalSize(total)
  }, [])

  useEffect(() => {
    loadVideos()
  }, [loadVideos])

  // ダウンロード完了時にリストを更新
  useEffect(() => {
    if (downloadState.status === 'completed') {
      loadVideos()
      setUrl('')
    }
  }, [downloadState.status, loadVideos])

  const handleDownload = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault()
      e?.stopPropagation()

      // ダウンロード中の重複実行を防止
      if (downloadState.isDownloading) {
        return
      }
      if (!url.trim()) {
        return
      }

      downloadVideo(url, quality)
    },
    [url, quality, downloadVideo, downloadState.isDownloading],
  )

  const handleDelete = async (video: StoredExternalVideo) => {
    try {
      await db.externalVideos.delete(video.id)
      toast.success('動画を削除しました')
      loadVideos()
      if (selectedVideo?.id === video.id) {
        setSelectedVideo(null)
      }
    } catch (error) {
      console.error('Failed to delete video:', error)
      toast.error('削除に失敗しました')
    }
  }

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event

      if (over && active.id !== over.id) {
        const oldIndex = videos.findIndex((v) => v.id === active.id)
        const newIndex = videos.findIndex((v) => v.id === over.id)

        const newVideos = arrayMove(videos, oldIndex, newIndex)
        setVideos(newVideos)

        // IndexedDBにorderを更新
        try {
          await Promise.all(newVideos.map((video, index) => db.externalVideos.update(video.id, { order: index })))
        } catch (error) {
          console.error('Failed to update order:', error)
          toast.error('並び順の更新に失敗しました')
          loadVideos()
        }
      }
    },
    [videos, loadVideos],
  )

  // 選択された動画のObject URLを生成
  const videoUrl = useMemo(() => {
    if (!selectedVideo) return null
    return URL.createObjectURL(selectedVideo.videoBlob)
  }, [selectedVideo])

  // クリーンアップ: Object URLを解放
  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl)
      }
    }
  }, [videoUrl])

  // 全画面プレイヤー
  if (selectedVideo && videoUrl) {
    return (
      <div className="fixed inset-0 bg-black z-50">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-10 text-white bg-black/50 hover:bg-black/70"
          onClick={() => setSelectedVideo(null)}
        >
          <X size={24} />
        </Button>
        <ReactPlayer
          src={videoUrl}
          controls
          width="100%"
          height="100%"
          playing
          controlsList="nodownload"
          disablePictureInPicture={false}
          onError={() => {
            const videoElement = document.querySelector('video')
            if (videoElement?.error) {
              const errorCodes: Record<number, string> = {
                1: 'ユーザーによる中断',
                2: 'ネットワークエラー',
                3: 'デコードエラー',
                4: '形式未サポート',
              }
              toast.error(`動画の再生に失敗: ${errorCodes[videoElement.error.code] || '不明なエラー'}`)
            } else {
              toast.error('動画の再生に失敗しました')
            }
          }}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <Header title="カスタム動画" onClose={goBack} />
      <div className="flex flex-col flex-1 m-4 gap-4">
        {isOnline && (
          <>
            {/* ダウンロードフォーム */}
            <div className="space-y-2">
              {/* 品質選択 */}
              <div className="flex gap-2">
                <Button
                  variant={quality === 'high' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setQuality('high')}
                  disabled={downloadState.isDownloading}
                  className="flex-1"
                >
                  高画質
                </Button>
                <Button
                  variant={quality === 'medium' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setQuality('medium')}
                  disabled={downloadState.isDownloading}
                  className="flex-1"
                >
                  中画質 (720p)
                </Button>
                <Button
                  variant={quality === 'low' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setQuality('low')}
                  disabled={downloadState.isDownloading}
                  className="flex-1"
                >
                  低画質 (480p)
                </Button>
              </div>

              <DownloadUrlForm
                url={url}
                onUrlChange={setUrl}
                onDownload={handleDownload}
                onCancel={cancelDownload}
                isDownloading={downloadState.isDownloading}
                placeholder="動画のURLを入力"
              />

              {/* ダウンロード進捗 */}
              {downloadState.isDownloading && (
                <DownloadProgress
                  message={downloadState.message}
                  progress={downloadState.progress}
                  speed={downloadState.speed}
                  eta={downloadState.eta}
                />
              )}
            </div>
          </>
        )}

        {/* 合計容量 */}
        <StorageUsage totalSize={totalSize} />

        {/* 保存された動画リスト */}
        <div className="flex-1 overflow-y-auto mw-full">
          {videos.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Download size={48} className="mb-2" />
              <p>保存された動画がありません</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={videos.map((v) => v.id)} strategy={verticalListSortingStrategy}>
                <SwipeableList className="w-full">
                  {videos.map((video, index) => (
                    <SortableVideoItem
                      key={video.id}
                      video={video}
                      onSelect={setSelectedVideo}
                      onInfo={setInfoVideo}
                      onDelete={handleDelete}
                      showSeparator={index < videos.length - 1}
                    />
                  ))}
                </SwipeableList>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* 動画情報ダイアログ */}
        <VideoInfoDialog video={infoVideo} onClose={() => setInfoVideo(null)} onSaved={loadVideos} />
      </div>
    </div>
  )
}
